import React from 'react'
import { Container, Group, Space, Text, Paper, Box, Anchor } from '@mantine/core'
import { Badge } from '@mantine/core'
import { TextHeading, TextHeadingAlignmentFix } from '@/components'
import { useMediumScreen } from '@/hooks'
import { BurnInputs } from './BurnInputs'
import { BurnResults } from './BurnResults'
import Link from 'next/link'
import { FEATURE_FLAGS } from '@/feature-flags'

const BurnEstimate: React.FC = () => {
  const { isMediumScreen } = useMediumScreen()

  return (
    <Container
      fluid={!isMediumScreen}
      size={isMediumScreen ? 996 : undefined}
      px={isMediumScreen ? 'xs' : 'sm'}
      pt={isMediumScreen ? 'xl' : 'sm'}>
      <Group spacing="md">
        <TextHeadingAlignmentFix>
          <TextHeading>Burn Estimator</TextHeading>
        </TextHeadingAlignmentFix>

        <Badge size="xs" color="pink" variant="filled">
          New
        </Badge>
      </Group>

      <Space h="xs" />

      <Text size="sm" sx={(t) => ({ color: t.colors.gray[7] })}>
        Fixed STRD supply of 100M. 1 STRD burned = 1 STRD-R minted.
      </Text>

      <Space h="xl" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMediumScreen ? '1fr 1fr' : '1fr',
          gap: isMediumScreen ? 24 : 16
        }}>
        <Paper p={isMediumScreen ? 'lg' : 'md'}>
          <BurnInputs />
        </Paper>

        <Paper>
          <BurnResults />
        </Paper>
      </Box>

      {FEATURE_FLAGS.BURN ? (
        <>
          <Space h="sm" />

          <Anchor component={Link} href="/burn" size="sm" sx={{ '&:hover': { textDecoration: 'none' } }}>
            <Paper px="sm" py="xs" radius="md" sx={(t) => ({ backgroundColor: t.colors.gray[9] })} className="group">
              <Group position="apart" noWrap>
                <Text size="sm" sx={(t) => ({ color: t.white })}>
                  Ready to cook?
                </Text>

                <Group spacing="xs">
                  <Text size="sm" weight={600} sx={(t) => ({ color: t.colors.pink[6] })}>
                    Burn
                  </Text>

                  <Box
                    sx={(t) => ({
                      color: t.colors.pink[6],
                      display: 'inline-block',
                      transform: 'translateX(0px) translateY(3px)',
                      transition: 'transform 350ms cubic-bezier(0.19, 1, 0.22, 1)',
                      '.group:hover &': { transform: 'translateX(4px) translateY(3px)' }
                    })}>
                    <RightArrow />
                  </Box>
                </Group>
              </Group>
            </Paper>
          </Anchor>
        </>
      ) : null}

      <Space h="lg" />

      <Box
        component="ul"
        sx={(t) => ({
          listStyle: 'disc',
          paddingLeft: 24,
          color: t.colors.gray[7],
          fontSize: t.fontSizes.sm,
          '& li': {
            marginBottom: 4
          }
        })}>
        <li>All values in tokens. Decimals accepted; outputs are rounded for display.</li>
        <li>STRD-R is minted 1:1 with STRD burned.</li>
        <li>Network burn auto-includes your burn for consistency.</li>
      </Box>
    </Container>
  )
}

const RightArrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  )
}

export { BurnEstimate }
